import { currentAuthToken } from "@homebound/auth-components";
import {
  BoundNumberField,
  Button,
  column,
  Css,
  GridColumn,
  Tooltip,
  GridDataRow,
  GridTable,
  ModalProps,
  RowStyles,
  simpleDataRows,
  SimpleHeaderAndData,
  useModal,
  useSnackbar,
} from "@homebound/beam";
import { ObjectConfig, ObjectState, useFormState } from "@homebound/form-state";
import { useCallback, useMemo, useState } from "react";
import { jsonToCSV } from "react-papaparse";
import { CsvUploader } from "src/components/CsvUploader";
import {
  LotInvestmentStatus,
  LotOverviewPageLotFragment,
  LotPartitionFragment,
  Maybe,
  SaveLotInput,
  SaveLotPartitionInput,
  useCreateDirectUnderwritingContractsMutation,
  useSaveLotMutation,
  useSaveLotPartitionsMutation,
} from "src/generated/graphql-types";
import { createImportCsvUrl } from "src/RouteUrls";
import { Percentage } from "src/components";
import { formatCentsToPrice } from "src/utils";
import { DateOnly } from "src/utils/dates";
import { BoundBeamDateField } from "src/components/BoundBeamDateField";

export type DirectUnderwritingLotPartitionsTableProps = {
  lotPartitions: LotPartitionFragment[];
  lot: LotOverviewPageLotFragment;
};

export function DirectUnderwritingLotPartitionsTable(props: DirectUnderwritingLotPartitionsTableProps) {
  const { lot, lotPartitions } = props;
  const { openModal } = useModal();

  const isActiveInvestment = lot.lotInvestmentStatus.code === LotInvestmentStatus.Active;

  // Lot-level form state for underwriting fields
  const lotFormConfig: ObjectConfig<LotFormInput> = useMemo(
    () => ({
      id: { type: "value" },
      underwrittenSiteAcquisitionMarkupBasisPoints: { type: "value" },
      underwrittenPurchasePriceInCents: { type: "value" },
      underwrittenFinancingCostInCents: { type: "value" },
      acquisitionDate: { type: "value" },
      underContractAmountInCents: { type: "value" },
      purchasePriceInCents: { type: "value" },
    }),
    [],
  );

  const [saveLot] = useSaveLotMutation();

  const lotFormState = useFormState({
    config: lotFormConfig,
    init: {
      input: lot,
      map: mapLotToForm,
    },
    autoSave: async (formState) => {
      const { id, ...changedFields } = formState.changedValue;
      if (Object.keys(changedFields).length > 0) {
        await saveLot({
          variables: { input: { id: lot.id, ...changedFields } },
        });
      }
    },
    readOnly: isActiveInvestment,
  });

  const formConfig: ObjectConfig<FormInput> = useMemo(
    () => ({
      id: { type: "value" },
      lotPartitions: {
        type: "list",
        config: lotPartitionConfig,
      },
    }),
    [],
  );

  const formState = useFormState({
    config: formConfig,
    init: {
      input: lot,
      map: mapToForm,
    },
    autoSave: saveLotPartitions,
  });
  const [saveLotPartition] = useSaveLotPartitionsMutation();
  const [createDirectUnderwritingContracts] = useCreateDirectUnderwritingContractsMutation();

  async function saveLotPartitions(formState: ObjectState<FormInput>) {
    console.log("Saving Lot Partitions", formState.changedValue);
    const changedLotPartitions =
      formState.changedValue.lotPartitions?.filter(
        (lp) =>
          lp.directUnderwritingSoftCostsInCents !== undefined ||
          lp.directUnderwritingHardCostsInCents !== undefined ||
          lp.directUnderwritingHardCostMarkupBasisPoints !== undefined ||
          lp.directUnderwritingStartDate !== undefined ||
          lp.underwrittenSalesPriceInCents !== undefined ||
          lp.underwrittenSalesCommissionBasisPoints !== undefined ||
          lp.underwrittenLotOwnershipBasisPoints !== undefined,
      ) || [];
    await Promise.all(
      changedLotPartitions.map(async (lp) => {
        // When underwrittenSalesPriceInCents changes, also update underwrittenSalesPriceWithoutAppreciationInCents to the same value
        // purchasePriceInCents is a read-only computed field, so it's not included in the input
        const { purchasePriceInCents, ...rest } = lp;
        const input =
          rest.underwrittenSalesPriceInCents !== undefined
            ? { ...rest, underwrittenSalesPriceWithoutAppreciationInCents: rest.underwrittenSalesPriceInCents }
            : rest;
        return saveLotPartition({
          variables: { input: { lotPartitions: [input] } },
        });
      }),
    );
  }

  const canBookDisabled = !lot.canContractDirectUnderwriting.allowed;
  const contractToBlueprintTooltip = canBookDisabled
    ? lot.canContractDirectUnderwriting.disabledReasons.map((reason) => reason.message).join("; ")
    : "Contract all outstanding Direct Underwriting to Blueprint";

  const { addError, clearErrors, csvContent, errors, loading, setCsvContent, upload } = useUploadDirectUnderwritingCsv(
    lot.id,
  );

  return (
    <>
      <div css={Css.mb2.p2.bgGray100.br8.$}>
        <div css={Css.smSb.mb1.$}>Lot-Level Underwriting</div>
        {isActiveInvestment && (
          <div css={Css.xsSb.gray600.mb2.$}>
            These fields cannot be edited because this lot is an active investment.
          </div>
        )}
        <div css={Css.df.gap2.$}>
          <div css={Css.w("200px").$}>
            <BoundNumberField
              label="Site Acquisition Markup %"
              type="basisPoints"
              field={lotFormState.underwrittenSiteAcquisitionMarkupBasisPoints}
            />
          </div>
          <div css={Css.w("200px").$}>
            <BoundNumberField
              label="Financing Cost"
              type="cents"
              field={lotFormState.underwrittenFinancingCostInCents}
            />
          </div>
          <div css={Css.w("200px").$}>
            <BoundBeamDateField label="Lot Acquisition Date" field={lotFormState.acquisitionDate} />
          </div>
          <div css={Css.w("250px").$}>
            <BoundNumberField
              label="Underwritten Purchase Price"
              type="cents"
              field={lotFormState.underwrittenPurchasePriceInCents}
              helperText={
                "The amount underwritten (Gross) for the purchase of this takedown - probably the same as Under Contract Amount (Gross)."
              }
            />
          </div>
          <div css={Css.w("250px").$}>
            <BoundNumberField
              label="Under Contract Amount (Gross)"
              type="cents"
              field={lotFormState.underContractAmountInCents}
            />
          </div>
          <div css={Css.w("200px").$}>
            <BoundNumberField
              label="Purchase Price (Net)"
              type="cents"
              field={lotFormState.purchasePriceInCents}
              helperText={"The Net Realized Amount paid"}
            />
          </div>
        </div>
      </div>

      <CsvUploader
        onError={addError}
        errors={errors}
        onDrop={(content) => {
          clearErrors();
          setCsvContent(
            jsonToCSV(
              content.map(({ data }) => data),
              { newline: "\n" }, // default is `\r\n` which breaks the parser on the backend
            ),
          );
        }}
      />
      <Button label="Import" disabled={!csvContent || loading} onClick={upload} />

      <GridTable
        id="underwritingLotPartitionsTable"
        rowStyles={rowStyles}
        columns={createPartitionColumns(formState, openModal)}
        rows={createPartitionRows(lotPartitions)}
        fallbackMessage="Partitions for this Lot will show here."
      />
      <Button
        variant="primary"
        disabled={canBookDisabled}
        tooltip={contractToBlueprintTooltip}
        onClick={async () => {
          await createDirectUnderwritingContracts({
            variables: {
              input: {
                lotId: lot.id,
              },
            },
          });
        }}
        label={"Confirm and Lock in Direct Underwriting to Blueprint"}
      />
    </>
  );
}

type Row = SimpleHeaderAndData<LotPartitionFragment>;

const rowStyles: RowStyles<Row> = {
  header: {},
  data: {},
};

function createPartitionRows(lotPartitions: LotPartitionFragment[]): GridDataRow<Row>[] {
  return simpleDataRows(lotPartitions);
}

function createPartitionColumns(
  formState: ObjectState<FormInput>,
  openModal: { (props: ModalProps): void },
): GridColumn<Row>[] {
  const idColumn = column<Row>({
    header: () => "Partition ID",
    data: ({ id }) => id,
    w: "100px",
  });

  const blueprintIdColumn = column<Row>({
    header: () => "Blueprint Project ID",
    data: ({ blueprintProjectId }) => blueprintProjectId,
    w: "100px",
  });

  const projectedStartDateColumn = column<Row>({
    header: () => "Projected Start Date",
    data: (row) => {
      const formStateField = formState.lotPartitions.rows.find((lp) => lp.id.value === row.id);
      return {
        value: () => row.directUnderwritingStartDate,
        content: () => <BoundBeamDateField field={formStateField!.directUnderwritingStartDate} />,
      };
    },
    w: "150px",
  });

  const softCostColumn = column<Row>({
    header: () => "Direct Soft Costs",
    data: (row) => {
      const formStateField = formState.lotPartitions.rows.find((lp) => lp.id.value === row.id);
      return {
        value: () => row.directUnderwritingSoftCostsInCents,
        content: () => <BoundNumberField type="cents" field={formStateField!.directUnderwritingSoftCostsInCents} />,
        tooltip:
          "The total of underwritten soft costs for this property that are NOT included at the community/subdivision level",
      };
    },
    w: "200px",
  });

  const hardCostColumn = column<Row>({
    header: () => "Direct Hard Costs",
    data: (row) => {
      const formStateField = formState.lotPartitions.rows.find((lp) => lp.id.value === row.id);
      return {
        value: () => row.directUnderwritingHardCostsInCents,
        content: () => <BoundNumberField type="cents" field={formStateField!.directUnderwritingHardCostsInCents} />,
        tooltip:
          "The total of underwritten hard costs for this property that are NOT included at the community/subdivision level",
      };
    },
    w: "200px",
  });

  const hardCostMarkupColumn = column<Row>({
    header: () => "Hard Cost Markup %",
    data: (row) => {
      const formStateField = formState.lotPartitions.rows.find((lp) => lp.id.value === row.id);
      return {
        value: () => row.directUnderwritingHardCostMarkupBasisPoints,
        content: () => (
          <BoundNumberField type="basisPoints" field={formStateField!.directUnderwritingHardCostMarkupBasisPoints} />
        ),
        tooltip: "The markup percentage for hard costs on this property",
      };
    },
    w: "200px",
  });

  const hardCostMarkupTotalColumn = column<Row>({
    header: () => "Hard Cost Markup Total",
    data: ({ directUnderwritingHardCostMarkupBasisPoints, directUnderwritingHardCostsInCents }) => {
      if (!directUnderwritingHardCostMarkupBasisPoints || !directUnderwritingHardCostsInCents) {
        return undefined;
      }
      return formatCentsToPrice(
        directUnderwritingHardCostsInCents * (directUnderwritingHardCostMarkupBasisPoints / 10000),
      );
    },
    w: "100px",
  });

  const totalLotSpecificColumn = column<Row>({
    header: () => "Lot Specific Underwritten Costs Total",
    data: ({
      directUnderwritingHardCostMarkupBasisPoints,
      directUnderwritingHardCostsInCents,
      directUnderwritingSoftCostsInCents,
    }) => {
      if (
        !directUnderwritingHardCostMarkupBasisPoints ||
        !directUnderwritingHardCostsInCents ||
        !directUnderwritingSoftCostsInCents
      ) {
        return undefined;
      }
      return formatCentsToPrice(
        directUnderwritingHardCostsInCents * (directUnderwritingHardCostMarkupBasisPoints / 10000) +
          directUnderwritingHardCostsInCents +
          directUnderwritingSoftCostsInCents,
      );
    },

    w: "200px",
  });

  const underwrittenContractedColumn = column<Row>({
    header: () => "Current Contracted Underwritten Costs Total",
    data: ({ underwrittenOpcoTotalCostInCents }) => {
      return formatCentsToPrice(underwrittenOpcoTotalCostInCents || 0);
    },
    w: "300px",
  });

  const underwrittenOwnershipPercentageColumn = column<Row>({
    header: () => "UW Ownership %",
    data: ({ id, canEditOwnershipBasisPoints, underwrittenLotOwnershipBasisPoints }) => {
      if (!canEditOwnershipBasisPoints.allowed) {
        const tooltip = canEditOwnershipBasisPoints.disabledReasons.map((r) => r.message).join("; ");
        return (
          <Tooltip title={tooltip}>
            <Percentage percent={underwrittenLotOwnershipBasisPoints / 100} />
          </Tooltip>
        );
      }
      const formStateField = formState.lotPartitions.rows.find((lp) => lp.id.value === id);
      return <BoundNumberField type="basisPoints" field={formStateField!.underwrittenLotOwnershipBasisPoints} />;
    },
    w: "130px",
  });

  const underwrittenOwnershipAmountColumn = column<Row>({
    header: () => "UW Ownership Amount",
    data: ({ purchasePriceInCents }) => {
      return formatCentsToPrice(purchasePriceInCents || 0);
    },
    w: "130px",
  });

  const exitGrossPriceColumn = column<Row>({
    header: () => "Exit Gross Sale Price",
    data: (row) => {
      const formStateField = formState.lotPartitions.rows.find((lp) => lp.id.value === row.id);
      return {
        value: () => row.underwrittenSalesPriceInCents,
        content: () => <BoundNumberField type="cents" field={formStateField!.underwrittenSalesPriceInCents} />,
        tooltip: "The underwritten gross sales price for this property",
      };
    },
    w: "200px",
  });

  const exitCommissionRateColumn = column<Row>({
    header: () => "Exit Commission %",
    data: (row) => {
      const formStateField = formState.lotPartitions.rows.find((lp) => lp.id.value === row.id);
      return {
        value: () => row.underwrittenSalesCommissionBasisPoints,
        content: () => (
          <BoundNumberField type="basisPoints" field={formStateField!.underwrittenSalesCommissionBasisPoints} />
        ),
        tooltip: "The underwritten percentage for exit commission on this property",
      };
    },
    w: "200px",
  });

  const exitNetPriceColumn = column<Row>({
    header: () => "Exit Net Sale Price",
    data: ({ underwrittenSalesPriceInCents, underwrittenSalesCommissionBasisPoints }) => {
      if (!underwrittenSalesPriceInCents) {
        return undefined;
      }
      return formatCentsToPrice(underwrittenSalesPriceInCents * (1 - underwrittenSalesCommissionBasisPoints / 10000));
    },
    w: "200px",
  });

  return [
    idColumn,
    blueprintIdColumn,
    projectedStartDateColumn,
    softCostColumn,
    hardCostColumn,
    hardCostMarkupColumn,
    hardCostMarkupTotalColumn,
    totalLotSpecificColumn,
    underwrittenContractedColumn,
    underwrittenOwnershipPercentageColumn,
    underwrittenOwnershipAmountColumn,
    exitGrossPriceColumn,
    exitCommissionRateColumn,
    exitNetPriceColumn,
  ];
}

type FormInput = SaveLotPartitionInput & {
  lotPartitions: Maybe<(SaveLotPartitionInput & { purchasePriceInCents: number })[]>;
};

type LotFormInput = Pick<
  SaveLotInput,
  | "id"
  | "underwrittenSiteAcquisitionMarkupBasisPoints"
  | "underwrittenPurchasePriceInCents"
  | "underwrittenFinancingCostInCents"
  | "underContractAmountInCents"
  | "purchasePriceInCents"
> & {
  acquisitionDate: DateOnly | null | undefined;
};

function mapLotToForm(lot: LotOverviewPageLotFragment): LotFormInput {
  return {
    id: lot.id,
    underwrittenSiteAcquisitionMarkupBasisPoints: lot.underwrittenSiteAcquisitionMarkupBasisPoints,
    underwrittenPurchasePriceInCents: lot.underwrittenPurchasePriceInCents,
    underwrittenFinancingCostInCents: lot.underwrittenFinancingCostInCents,
    acquisitionDate: lot.acquisitionDate ? new DateOnly(lot.acquisitionDate) : undefined,
    underContractAmountInCents: lot.underContractAmountInCents,
    purchasePriceInCents: lot.purchasePriceInCents,
  };
}

function mapToForm(lot: LotOverviewPageLotFragment): FormInput {
  return {
    id: lot.id,
    lotPartitions: mapLotPartitionsToForm(lot.partitions),
  };
}

export function mapLotPartitionsToForm(lotPartitions: Maybe<LotPartitionFragment[]>) {
  return lotPartitions?.map((lp) => {
    return {
      id: lp.id,
      directUnderwritingSoftCostsInCents: lp.directUnderwritingSoftCostsInCents,
      directUnderwritingHardCostsInCents: lp.directUnderwritingHardCostsInCents,
      directUnderwritingHardCostMarkupBasisPoints: lp.directUnderwritingHardCostMarkupBasisPoints,
      directUnderwritingStartDate: lp.directUnderwritingStartDate
        ? new DateOnly(lp.directUnderwritingStartDate)
        : undefined,
      underwrittenSalesPriceInCents: lp.underwrittenSalesPriceInCents,
      underwrittenSalesCommissionBasisPoints: lp.underwrittenSalesCommissionBasisPoints,
      underwrittenLotOwnershipBasisPoints: lp.underwrittenLotOwnershipBasisPoints,
      purchasePriceInCents: lp.purchasePriceInCents || 0,
    };
  });
}

export const lotPartitionConfig: ObjectConfig<SaveLotPartitionInput & { purchasePriceInCents: number }> = {
  id: { type: "value" },
  directUnderwritingSoftCostsInCents: { type: "value" },
  directUnderwritingHardCostsInCents: { type: "value" },
  directUnderwritingHardCostMarkupBasisPoints: { type: "value" },
  directUnderwritingStartDate: { type: "value" },
  underwrittenSalesPriceInCents: { type: "value" },
  underwrittenSalesCommissionBasisPoints: { type: "value" },
  underwrittenLotOwnershipBasisPoints: { type: "value" },
  purchasePriceInCents: { type: "value" },
};

function useUploadDirectUnderwritingCsv(lotId: string) {
  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [csvContent, setCsvContent] = useState("");
  const { triggerNotice } = useSnackbar();

  const addError = (err: unknown) => setErrors((existing) => existing.concat(String(err)));
  const clearErrors = () => setErrors([]);

  const upload = useCallback(async () => {
    if (loading) return;
    setLoading(true);

    const response = await fetch(`${createImportCsvUrl("directUnderwritingUpload")}&lotId=${lotId}`, {
      method: "POST",
      headers: { "content-type": "text/csv", Authorization: `Bearer ${await currentAuthToken()}` },
      body: csvContent,
    });

    if (response.status !== 200) {
      // Backend may have identified errors in the CSV (Like unfilled columns or poorly-formatted numbers)
      // so pop them into the Errors array here.
      const { message } = await response.json();
      if (message) addError(message);
      setLoading(false);
      return;
    }

    triggerNotice({ message: "Direct Underwriting Information Uploaded Successfully" });
    // Just reload the page to see the new data - come back to this later and do a better job of updating the UI
    window.location.reload();
  }, [loading, lotId, csvContent, triggerNotice]);

  return { addError, clearErrors, csvContent, errors, loading, setCsvContent, upload };
}
